/**
 * Supply crates (65.9; the owner, Oct 10: "limited medical supplies" — so limited means walking back
 * for more). Each team starts with a crate near its first unit (match/setup.ts), a `supply` structure
 * holding a finite stock of bandages and spare magazines (SUPPLY). E in reach restocks what you are
 * missing from it — bandages to the full issue, spare rounds for any weapon that keeps a reserve, a
 * magazine's worth at a time — and the crate runs down; an empty crate gives nothing, another team's
 * crate nothing either. Bots out of bandages with nobody in sight walk to their crate (actionBot).
 *
 * 65.10: crates at the capture points too — a point's crate belongs to whoever holds the point, and
 * refills while they hold it (SUPPLY.point), so holding ground keeps a side supplied. With ammo
 * reserves on (GameConfig.ammoReserves, core/weapons.ts) the rifle's and the pistol's spare rounds
 * come from crates as well.
 *
 * Pure; the Match steps it (`stepSupplies` after the seats, `refillPointCrates` on its interval).
 */
import type { GameState, GameConfig, PlayerState, StructureState, GroundWeaponKind, Vec3 } from '../types';
import { vecAdd, vecCross, vecNormalize, vecScale } from '../math';
import { dcos, dsin } from './dmath';
import { standingWithJob } from './body';
import { carrierLoad } from './carrierLoad';
import { SUPPLY, GROUND_WEAPONS, BANDAGE, GUN_STOCK } from '../constants';
import { PERSON_BODY_HEIGHT, personSetOf, type PersonSet } from './scaleProfile';
import { arcDistance } from './gameState';
import { bandagesLeft } from './medical';
import { reserveLeft, weaponSpec } from './weapons';
import { gunStockOn, shellsShort } from './seats';
import { defaultOn } from './defaultOn';

/** Decision 128: the shells a person's team's guns on its planet are short of their stock, all told (0 without a stock). */
export function teamShellsShort(state: Pick<GameState, 'structures' | 'players' | 'config'>, p: Pick<PlayerState, 'teamIndex' | 'planetIndex'>): number {
  if (!gunStockOn(state)) return 0;
  // (62.60 lever 1: a pure function of the structures, the players and the config — kept per team and planet while those
  // are the same arrays, so a tick's bots read it once a team, not once each)
  const key = `${p.teamIndex}|${p.planetIndex}`;
  const m = shellsMemo.get(state.structures);
  const hit = m && m.players === state.players && m.config === state.config ? m.byTeam.get(key) : undefined;
  if (hit !== undefined) return hit;
  const short = teamShellsShortOf(state, p);
  if (!m || m.players !== state.players || m.config !== state.config) shellsMemo.set(state.structures, { players: state.players, config: state.config, byTeam: new Map([[key, short]]) });
  else m.byTeam.set(key, short);
  return short;
}
const shellsMemo = new WeakMap<readonly StructureState[], { players: readonly PlayerState[]; config: unknown; byTeam: Map<string, number> }>();
function teamShellsShortOf(state: Pick<GameState, 'structures' | 'players' | 'config'>, p: Pick<PlayerState, 'teamIndex' | 'planetIndex'>): number {
  let short = 0;
  for (const s of state.structures) {
    if (s.health <= 0 || s.planetIndex !== p.planetIndex || !s.seats?.[0]) continue;
    if (state.players.find(q => q.id === s.ownerId)?.teamIndex !== p.teamIndex) continue;
    short += shellsShort(state, s.seats[0]);
  }
  for (const q of state.players) if (q.teamIndex === p.teamIndex && q.alive && q.shellLoad) short -= q.shellLoad;   // already on the way
  return Math.max(0, short);
}

const KINDS = Object.keys(GROUND_WEAPONS) as GroundWeaponKind[];
type Cfg = Pick<GameConfig, 'ammoReserves'> & Partial<Pick<GameConfig, 'rocketStock' | 'rocketTeams' | 'gl40Stock'>> | undefined;
/** 65.29 (2): with the rocket stock on, the tube's spare comes from the side's stock (core/rocketStock.ts), not a crate's magazines. */
const crateServes = (k: GroundWeaponKind, config: Cfg) => !(k === 'rocket' && config?.rocketStock) && !(k === 'gl40' && defaultOn(config, 'gl40Stock'));   // (65.31 step 2: the 40 mm's likewise)
/** S7, per side (GameConfig.rocketTeams): a person whose side carries no tube is never short of a rocket (no walk to a crate for one). */
const carries = (k: GroundWeaponKind, config: Cfg, team: number | undefined) => k !== 'rocket' || config?.rocketTeams === undefined || team === undefined || config.rocketTeams.includes(team);
type Sites = Pick<GameState, 'modeState'>;

/** The reach of a crate, in world units at this scale. */
export function supplyReach(state: Pick<GameState, 'config'>): number {
  return SUPPLY.reach * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/** What a person is missing: bandages short of the issue, and spare rounds short of each reserve (weapons with one). */
export function supplyNeeds(p: Pick<PlayerState, 'bandages' | 'reserves'> & Partial<Pick<PlayerState, 'teamIndex'>>, ps: Parameters<typeof weaponSpec>[0], config?: Cfg): { bandages: number; rounds: number; byWeapon: Partial<Record<GroundWeaponKind, number>> } {
  const byWeapon: Partial<Record<GroundWeaponKind, number>> = {};
  let rounds = 0;
  for (const k of KINDS) {
    const spec = weaponSpec(ps, k, config);
    if (spec.reserve === undefined || !crateServes(k, config) || !carries(k, config, p.teamIndex)) continue;
    const short = spec.reserve - reserveLeft(p, k, spec);
    if (short > 0) { byWeapon[k] = short; rounds += short; }
  }
  return { bandages: BANDAGE.carried - bandagesLeft(p), rounds, byWeapon };
}

/** Low enough to walk back for more: no bandages left, or some weapon's spares under one magazine. */
export function supplyLow(p: Pick<PlayerState, 'bandages' | 'reserves'> & Partial<Pick<PlayerState, 'teamIndex'>>, ps: Parameters<typeof weaponSpec>[0], config?: Cfg): boolean {
  if (bandagesLeft(p) <= 0) return true;
  return KINDS.some(k => { const spec = weaponSpec(ps, k, config); return spec.reserve !== undefined && crateServes(k, config) && carries(k, config, p.teamIndex) && reserveLeft(p, k, spec) < spec.magazine; });
}

/** A crate with anything left in it (DECISIONS 406: an APC's or M113's load too, core/carrierLoad.ts). */
export function crateStocked(s: Pick<StructureState, 'type' | 'supply' | 'health'>): boolean {
  return (standingWithJob(s, 'supply') || carrierLoad(s)) && !!s.supply && s.supply.bandages + s.supply.magazines > 0;
}

/** Decision 128: a crate with shells left for its team's guns. */
export function crateHasShells(s: Pick<StructureState, 'type' | 'supply' | 'health'>): boolean {
  return standingWithJob(s, 'supply') && !!s.supply && (s.supply.shells ?? 0) > 0;
}

/** The team a crate serves: its own team's, or — a point's crate — whoever holds the point (null while nobody does). */
export function crateTeam(state: Sites, s: Pick<StructureState, 'supply'>): number | null {
  if (!s.supply) return null;
  if (s.supply.site === undefined) return s.supply.teamIndex;
  return state.modeState.points?.sites.find(x => x.id === s.supply!.site)?.owner ?? null;
}

/** The nearest stocked crate serving `p`'s team within `maxArc` (world units) of it on its planet, or null. */
export function nearestTeamCrate(state: Pick<GameState, 'structures' | 'planets' | 'modeState'>, p: Pick<PlayerState, 'teamIndex' | 'planetIndex' | 'surfaceDir'>, maxArc: number, stocked: (s: StructureState) => boolean = crateStocked): StructureState | null {
  return nearestOf(state, p, maxArc, stocked, true);
}

/**
 * DECISIONS 242 (the owner: "we can keep the flags indicating whos supply is whos but anybody should be able to take from
 * the supply dumps"): the nearest stocked crate of any side within `maxArc` of `p` on its planet, or null — whose it is
 * (crateTeam) still says whose flag it flies and whose stock it hands out (crateSide).
 */
export function nearestCrate(state: Pick<GameState, 'structures' | 'planets' | 'modeState'>, p: Pick<PlayerState, 'teamIndex' | 'planetIndex' | 'surfaceDir'>, maxArc: number, stocked: (s: StructureState) => boolean = crateStocked): StructureState | null {
  return nearestOf(state, p, maxArc, stocked, false);
}

/** 242: the side whose stock a crate hands out (the grenades, the rockets, the 40 mm's): its own; a point's nobody holds, the taker's. */
export function crateSide(state: Sites, s: Pick<StructureState, 'supply'>, p: Pick<PlayerState, 'teamIndex'>): number {
  return crateTeam(state, s) ?? p.teamIndex;
}

function nearestOf(state: Pick<GameState, 'structures' | 'planets' | 'modeState'>, p: Pick<PlayerState, 'teamIndex' | 'planetIndex' | 'surfaceDir'>, maxArc: number, stocked: (s: StructureState) => boolean, ownSide: boolean): StructureState | null {
  const planet = state.planets[p.planetIndex];
  if (!planet) return null;
  let best: StructureState | null = null, bestD = Infinity;
  for (const s of state.structures) {
    if (s.planetIndex !== p.planetIndex || !stocked(s) || (ownSide && crateTeam(state, s) !== p.teamIndex)) continue;
    const d = arcDistance(p.surfaceDir, s.surfaceDir, planet.radius);
    if (d <= maxArc && (d < bestD || (d === bestD && best !== null && s.id < best.id))) { bestD = d; best = s; }
  }
  return best;
}

/** A stocked crate of any side within reach of `p`, or null (242: the dumps are open to all). */
export function crateInReach(state: Pick<GameState, 'structures' | 'planets' | 'config' | 'modeState'>, p: Pick<PlayerState, 'teamIndex' | 'planetIndex' | 'surfaceDir'>, stocked: (s: StructureState) => boolean = crateStocked): StructureState | null {
  return nearestCrate(state, p, supplyReach(state), stocked);
}

export type SupplyEvent = { kind: 'resupplied'; unitId: number; structureId: number; bandages: number; magazines: number; rounds: number; shells?: number; drones?: number; charges?: number };

/**
 * 65.9: one tick's restocks, from each unit's intent: everyone asking (`resupply`) on their feet, out of any seat and
 * within reach of a stocked crate (242: any side's) takes what they are missing, as far as the crate goes — spare
 * rounds a magazine's worth at a time — in unit order, so the first to ask takes a contested last bandage. Returns
 * null when nothing changed.
 */
export function stepSupplies(state: Pick<GameState, 'players' | 'structures' | 'planets' | 'config' | 'modeState'>, asks: ReadonlyArray<boolean | undefined>, ps: PersonSet): { players: PlayerState[]; structures: StructureState[]; events: SupplyEvent[] } | null {
  let players: PlayerState[] | null = null, structures: StructureState[] | null = null;
  const events: SupplyEvent[] = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = (players ?? state.players)[i];
    if (!asks[i] || !p.alive || p.airborne || p.mount) continue;
    const here = { ...state, structures: structures ?? state.structures };
    const crate = crateInReach(here, p) ?? crateInReach(here, p, crateHasShells);
    if (!crate) continue;
    const need = supplyNeeds(p, ps, state.config);
    const bandages = Math.min(need.bandages, crate.supply!.bandages);
    let rounds = 0, magazines = 0;
    const reserves = { ...(p.reserves ?? {}) };
    let left = crate.supply!.magazines;
    for (const k of KINDS) {
      const short = need.byWeapon[k] ?? 0;
      if (short <= 0 || left <= 0) continue;
      const spec = weaponSpec(ps, k, state.config);
      const mags = Math.min(left, Math.ceil(short / spec.magazine)), give = Math.min(short, mags * spec.magazine);
      reserves[k] = reserveLeft(p, k, spec) + give;
      left -= mags; magazines += mags; rounds += give;
    }
    // decision 128: a load of shells for the team's guns, when one is short and nothing is carried yet
    const shells = !p.shellLoad && (crate.supply!.shells ?? 0) > 0 ? Math.min(GUN_STOCK.load, crate.supply!.shells!, teamShellsShort(state, p)) : 0;
    // 65.19: drones and charges back up to the person's issue, as far as the crate goes
    const drones = p.payloadIssue ? Math.min(Math.max(0, p.payloadIssue.drones - (p.drones ?? 0)), crate.supply!.drones ?? 0) : 0;
    const charges = p.payloadIssue ? Math.min(Math.max(0, p.payloadIssue.charges - (p.charges ?? 0)), crate.supply!.charges ?? 0) : 0;
    if (bandages <= 0 && rounds <= 0 && shells <= 0 && drones <= 0 && charges <= 0) continue;
    players ??= state.players.slice();
    structures ??= state.structures.slice();
    players[i] = { ...p, ...(bandages > 0 ? { bandages: bandagesLeft(p) + bandages } : {}), ...(rounds > 0 ? { reserves } : {}), ...(shells > 0 ? { shellLoad: shells } : {}),
      ...(drones > 0 ? { drones: (p.drones ?? 0) + drones } : {}), ...(charges > 0 ? { charges: (p.charges ?? 0) + charges } : {}) };
    structures[structures.findIndex(s => s.id === crate.id)] = { ...crate, supply: { ...crate.supply!, bandages: crate.supply!.bandages - bandages, magazines: left, ...(shells > 0 ? { shells: crate.supply!.shells! - shells } : {}), ...(drones > 0 ? { drones: (crate.supply!.drones ?? 0) - drones } : {}), ...(charges > 0 ? { charges: (crate.supply!.charges ?? 0) - charges } : {}) } };
    events.push({ kind: 'resupplied', unitId: p.id, structureId: crate.id, bandages, magazines, rounds, ...(shells > 0 ? { shells } : {}), ...(drones > 0 ? { drones } : {}), ...(charges > 0 ? { charges } : {}) });
  }
  return players ? { players: players!, structures: structures!, events } : null;
}

/** 65.10: where a capture point's crate stands — two people off its centre (inside the ring: standing there is holding it), at an angle by the point's id. */
export function pointCrateDir(state: Pick<GameState, 'planets' | 'config'>, site: { id: number; planetIndex: number; surfaceDir: Vec3 }): Vec3 {
  const planet = state.planets[site.planetIndex], n = vecNormalize(site.surfaceDir);
  const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u), ang = site.id * 2.4 + 0.7;
  const arc = (2 * PERSON_BODY_HEIGHT * personSetOf(state).k) / planet.radius;
  return vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(vecAdd(vecScale(u, dcos(ang)), vecScale(v, dsin(ang))), dsin(arc))));
}

/**
 * 65.10: every point crate whose point is held gains a bandage and a magazine, up to its stock (SUPPLY.point). The
 * Match calls it every SUPPLY.point.refillS seconds of the action clock. Returns null when nothing changed.
 */
export function refillPointCrates(state: Pick<GameState, 'structures' | 'modeState'>): StructureState[] | null {
  let out: StructureState[] | null = null;
  state.structures.forEach((s, i) => {
    if (!standingWithJob(s, 'supply') || !s.supply || s.supply.site === undefined || crateTeam(state, s) === null) return;
    const bandages = Math.min(SUPPLY.point.bandages, s.supply.bandages + 1), magazines = Math.min(SUPPLY.point.magazines, s.supply.magazines + 1);
    if (bandages === s.supply.bandages && magazines === s.supply.magazines) return;
    (out ??= state.structures.slice())[i] = { ...s, supply: { ...s.supply, bandages, magazines } };
  });
  return out;
}
